'use client';

import {
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetCampaignResultsDetailed } from '~/entities/Campaign/hooks';
import {
  StyledContainer,
  StyledHeaderBoxTableCell,
  StyledHeaderTableCell,
  StyledTable,
  StyledTableContainer,
} from '~/shared';
import SortIcon from '~/shared/icons/SortIcon';
import type { CampaignResultsDetailedModelView } from '~/types';

import { useBannerSort } from '../hooks';

import { BannerRow } from './Row';

export function BannersList({
  getCampaignResultsDetailedQuery,
}: {
  getCampaignResultsDetailedQuery: ReturnType<
    typeof useGetCampaignResultsDetailed
  >;
}) {
  const {
    sortedBanners,
    isSorting,
    sortByOrderName,
    sortByOrderImpressions,
    sortByOrderClicks,
    sortByOrderCTR,
    sortByOrderCPM,
    sortByOrderCPC,
    setSortByOrderName,
    setSortByOrderImpressions,
    setSortByOrderClicks,
    setSortByOrderCTR,
    setSortByOrderCPM,
    setSortByOrderCPC,
    toggleSort,
  } = useBannerSort(getCampaignResultsDetailedQuery.data);

  const showSortIcons = sortedBanners.length > 1;

  return (
    <StyledContainer
      sx={theme => ({
        width: '100%',
        gridColumn: 'span 3',
        borderRadius: '8px',
        paddingTop: theme.spacing(3),
        [theme.breakpoints.down('lg')]: { gridColumn: 'span 2' },
        [theme.breakpoints.down('md')]: { gridColumn: 'span 1' },
      })}
    >
      <StyledTableContainer>
        <StyledTable aria-label="Tabela de Banners">
          {!isSorting &&
            !getCampaignResultsDetailedQuery.isLoading &&
            !getCampaignResultsDetailedQuery.isFetching && (
              <TableHead>
                <TableRow>
                  <StyledHeaderTableCell sx={{ width: '0' }} align="center" />
                  <StyledHeaderTableCell
                    sx={{
                      minWidth: '200px',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell
                        sx={{ justifyContent: 'flex-start' }}
                      >
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          Nome / Formato
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderName}
                          onClick={() =>
                            toggleSort(sortByOrderName, setSortByOrderName)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        Nome / Formato
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    <Typography variant="body2" component="p" fontWeight={500}>
                      Status
                    </Typography>
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    <Typography variant="body2" component="p" fontWeight={500}>
                      Plataforma
                    </Typography>
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          Impressões
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderImpressions}
                          onClick={() =>
                            toggleSort(
                              sortByOrderImpressions,
                              setSortByOrderImpressions
                            )
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        Impressões
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          Cliques
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderClicks}
                          onClick={() =>
                            toggleSort(sortByOrderClicks, setSortByOrderClicks)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        Cliques
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          CTR
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderCTR}
                          onClick={() =>
                            toggleSort(sortByOrderCTR, setSortByOrderCTR)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        CTR
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          CPM
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderCPM}
                          onClick={() =>
                            toggleSort(sortByOrderCPM, setSortByOrderCPM)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        CPM
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    {showSortIcons ? (
                      <StyledHeaderBoxTableCell>
                        <Typography
                          variant="body2"
                          component="p"
                          fontWeight={500}
                        >
                          CPC
                        </Typography>
                        <SortIcon
                          sortOrder={sortByOrderCPC}
                          onClick={() =>
                            toggleSort(sortByOrderCPC, setSortByOrderCPC)
                          }
                        />
                      </StyledHeaderBoxTableCell>
                    ) : (
                      <Typography
                        variant="body2"
                        component="p"
                        fontWeight={500}
                      >
                        CPC
                      </Typography>
                    )}
                  </StyledHeaderTableCell>
                  <StyledHeaderTableCell>
                    <Typography variant="body2" component="p" fontWeight={500}>
                      Vendas
                    </Typography>
                  </StyledHeaderTableCell>
                </TableRow>
              </TableHead>
            )}

          <TableBody>
            {isSorting && (
              <TableRow>
                <TableCell
                  component="th"
                  scope="row"
                  colSpan={10}
                  align="center"
                >
                  <CircularProgress size={15} /> Carregando...
                </TableCell>
              </TableRow>
            )}

            {!isSorting &&
              (getCampaignResultsDetailedQuery.isLoading ||
                getCampaignResultsDetailedQuery.isFetching) && (
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    colSpan={10}
                    align="center"
                  >
                    <CircularProgress size={15} /> Carregando
                  </TableCell>
                </TableRow>
              )}

            {!isSorting &&
              !getCampaignResultsDetailedQuery.isLoading &&
              !getCampaignResultsDetailedQuery.isFetching &&
              sortedBanners.map(
                (banner: CampaignResultsDetailedModelView[number]) => {
                  return <BannerRow key={banner.key} banner={banner} />;
                }
              )}

            {!isSorting &&
              !getCampaignResultsDetailedQuery.isLoading &&
              !getCampaignResultsDetailedQuery.isFetching &&
              sortedBanners.length === 0 && (
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    colSpan={10}
                    align="center"
                  >
                    Não possui banners
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </StyledContainer>
  );
}
